import {
  EntityStringIdRouteCodec,
  IdentityCodec,
  LanguageCodec,
  PositiveIntegerCodec,
  makeRouteCodec,
  SlugCodec,
} from 'app/constants/Routes/codecs';
import DynamicRoute from 'app/constants/Routes/DynamicRoute';
import StaticRoute from 'app/constants/Routes/StaticRoute';
import { EntityStringId } from 'app/types/entity';
import { LanguageId } from '../enums/Language/data';

const ADMIN_PREFIX = '/admin';
const EDITOR_PREFIX = '/editor';
const DAILY_NEWS_PREFIX = '/daily-news';
const IFRAME_PREFIX = '/iframes';
const RTC_PREFIX = '/calls';
const MEETING_PREFIX = '/meeting';
const SLACK_INTEGRATION_PREFIX = `${MEETING_PREFIX}/slack-integration`;

export const BaseRoute = new StaticRoute({
  route: '',
  syncable: true,
});

export const RootRoute = new StaticRoute({
  route: '/',
  syncable: true,
});

export const DashboardRoute = new StaticRoute({
  route: '/dashboard',
  syncable: true,
});

export const OauthDelegateRoute = new StaticRoute({
  route: '/oauth/authorize', // as mandated by the oauth spec
  syncable: false,
});

export const OauthLoginCallback = new StaticRoute({
  route: '/login/callback',
  syncable: false,
});

export const OauthLoginPopupCallback = new StaticRoute({
  route: '/login/popup-callback',
  syncable: false,
});

export const IknowLoginCallback = new StaticRoute({
  route: '/login/iknow-callback',
  syncable: false,
});

export const OauthAttachCallback = new StaticRoute({
  route: '/users/external-auth/callback',
  syncable: false,
});

export const DmmLoginCallback = new StaticRoute({
  route: '/users/dmm-login/callback',
  syncable: false,
});

export const LoginRoute = new StaticRoute({
  route: '/login',
  syncable: false,
});

export const LogoutRoute = new StaticRoute({
  route: '/logout',
  syncable: false,
});

export const SignupRoute = new StaticRoute({
  route: '/signup',
  syncable: false,
});

export const ForgotPasswordRoute = new StaticRoute({
  route: '/forgot-password',
  syncable: false,
});

export const ResetPasswordRoute = new StaticRoute({
  route: '/users/reset-password',
  syncable: false,
});

export const SendUnlockRoute = new StaticRoute({
  route: '/unlock',
  syncable: false,
});

export const UnlockRoute = new StaticRoute({
  route: '/users/unlock',
  syncable: false,
});

export const SendConfirmationRoute = new StaticRoute({
  route: '/confirm',
  syncable: false,
});

export const ConfirmRoute = new StaticRoute({
  route: '/users/confirm',
  syncable: false,
});

/**
 * Old route for market redirect.
 * @deprecated replaced by {@link WrongBrandRoute}
 */
export const WrongMarketRoute = new StaticRoute({
  route: '/wrong-market',
  syncable: false,
});

export const WrongBrandRoute = new StaticRoute({
  route: '/wrong-brand',
  syncable: false,
});

export const CategoriesListRoute = new DynamicRoute<{
  languageId: LanguageId,
}>({
  route: `${ADMIN_PREFIX}/categories/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const CurriculaListRoute = new DynamicRoute<{ organizationId: EntityStringId }>({
  route: `${EDITOR_PREFIX}/organization/:organizationId/curricula`,
  syncable: true,
  codec: makeRouteCodec({
    organizationId: EntityStringIdRouteCodec,
  }),
});

export const BrandCurriculumEditRoute = new DynamicRoute<{ languageId: LanguageId, brandId: EntityStringId }>({
  route: `${EDITOR_PREFIX}/brand/:brandId/curriculum/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
    brandId: EntityStringIdRouteCodec,
  }),
});

export const EditCategoryRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/categories/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const EditorCoursesRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${EDITOR_PREFIX}/courses/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const EditCategoryCoursesRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/categories/edit/:id/courses`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const EditCourseRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/courses/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const EditCourseLessonsRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/courses/edit/:id/lessons`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const BannersRoute = new StaticRoute({
  route: `${EDITOR_PREFIX}/banners`,
  syncable: true,
});

export const CreateBannersRoute = new StaticRoute({
  route: `${EDITOR_PREFIX}/banners/create`,
  syncable: true,
});

export const EditBannersRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/banners/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const ViewCurrentPublishedLessonRoute = new DynamicRoute<{ id: EntityStringId, slug: string }>({
  route: '/lessons/:slug/:id',
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
    slug: SlugCodec,
  }),
});

export const ViewPublishedLessonRoute = new DynamicRoute<{ id: EntityStringId, slug: string }>({
  route: '/lessons/view/:slug/:id',
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
    slug: SlugCodec,
  }),
});

export const EditorLessonsRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${EDITOR_PREFIX}/lessons/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const EditorLessonTemplatesRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${EDITOR_PREFIX}/lesson-templates/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const EditLessonRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/lessons/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const ViewLessonRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/lessons/view/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const PublishLessonRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/lessons/publish/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const CommonTextsRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${EDITOR_PREFIX}/common-texts/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const EditCommonTextRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/common-texts/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const DialogSpeakersRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${EDITOR_PREFIX}/dialog-speakers/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const EditDialogSpeakerRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${EDITOR_PREFIX}/dialog-speakers/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const CreateUserRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/users/create`,
  syncable: false,
});

export const EditUserRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/users/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const ViewUserRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/users/view/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const EditProfileRoute = new StaticRoute({
  route: '/profile',
  syncable: false,
});

export const IncompleteProfileRoute = new StaticRoute({
  route: '/incomplete-profile',
  syncable: false,
});

export const EditBlockedEmailDomainsRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/blocked-email-domains`,
  syncable: false,
});

export const UsersRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/users`,
  syncable: true,
});

export const CreateStaffMemberRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/staff-members/create`,
  syncable: false,
});

export const EditStaffMemberRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/staff-members/edit/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const StaffMembersRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/staff-members`,
  syncable: true,
});

export const StaffProfileRoute = new StaticRoute({
  route: '/profile/staff',
  syncable: false,
});

export const EditorTeachersRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/teachers`,
  syncable: true,
});

export const ViewTeacherRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/teachers/view/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const DailyNewsRoute = new StaticRoute({
  route: `${DAILY_NEWS_PREFIX}`,
  syncable: true,
});

export const DailyNewsCategoryRoute = new DynamicRoute<{ category: string }>({
  route: `${DAILY_NEWS_PREFIX}/category/:category`,
  syncable: true,
  codec: makeRouteCodec({
    category: IdentityCodec,
  }),
});

// Alias for published lesson but using different theme
export const DailyNewsArticleRoute = new DynamicRoute<{ id: EntityStringId, slug: string }>({
  route: `${DAILY_NEWS_PREFIX}/article/:slug/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
    slug: SlugCodec,
  }),
});

export const DailyNewsSpecificArticleRoute = new DynamicRoute<{ id: EntityStringId, slug: string }>({
  route: `${DAILY_NEWS_PREFIX}/article/view/:slug/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
    slug: SlugCodec,
  }),
});

export const DailyNewsSearchRoute = new StaticRoute({
  route: `${DAILY_NEWS_PREFIX}/search`,
  syncable: true,
});

export const FullScreenCallRoute = new StaticRoute({
  route: `${RTC_PREFIX}/full-screen`,
  syncable: true,
});

export const TestCallRoute = new StaticRoute({
  route: `${RTC_PREFIX}/test`,
  syncable: true,
});

export const PdfCallRoute = new DynamicRoute<{ position: number }>({
  route: `${RTC_PREFIX}/pdf/:position`,
  syncable: true,
  codec: makeRouteCodec({
    position: PositiveIntegerCodec,
  }),
});

export const ScreenSharingContentViewRoute = new DynamicRoute<{
  participantId: EntityStringId,
}>({
  route: `${RTC_PREFIX}/screen/:participantId`,
  syncable: true,
  codec: makeRouteCodec({
    participantId: EntityStringIdRouteCodec,
  }),
});

export const CallEndedRoute = new DynamicRoute<{ id: EntityStringId, type: string }>({
  route: `${RTC_PREFIX}/end/:type/:id`,
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
    type: IdentityCodec,
  }),
});

export const LessonBookingRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: '/lesson-booking/:id',
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const MeetingLandingRoute = new StaticRoute({
  route: MEETING_PREFIX,
  syncable: false,
});

export const MeetingBookingNewRoute = new StaticRoute({
  route: `${MEETING_PREFIX}/new`,
  syncable: false,
});

export const MeetingBookingRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${MEETING_PREFIX}/:id`,
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const MeetingJoinRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${MEETING_PREFIX}/join/:id`,
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

const MEETING_BOOKINGS_ADMIN_PREFIX = `${ADMIN_PREFIX}/meeting-bookings`;

export const MeetingBookingsAdminRoute = new StaticRoute({
  route: MEETING_BOOKINGS_ADMIN_PREFIX,
  syncable: true,
});

export const BrandMeetingsRoute = new DynamicRoute<{
  brandId: EntityStringId,
}>({
  route: '/brands/:brandId/meetings',
  syncable: true,
  codec: makeRouteCodec({
    brandId: EntityStringIdRouteCodec,
  }),
});

const LESSON_BOOKINGS_ADMIN_PREFIX = `${ADMIN_PREFIX}/lesson-bookings`;

export const LessonBookingsAdminRoute = new StaticRoute({
  route: LESSON_BOOKINGS_ADMIN_PREFIX,
  syncable: true,
});

export const CreateLessonBookingRoute = new StaticRoute({
  route: `${LESSON_BOOKINGS_ADMIN_PREFIX}/new`,
  syncable: false,
});

export const ViewLessonBookingRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${LESSON_BOOKINGS_ADMIN_PREFIX}/:id`,
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const LessonBookingTranscriptRoute = new DynamicRoute<{ lessonBookingId: EntityStringId }>({
  route: `${LESSON_BOOKINGS_ADMIN_PREFIX}/:lessonBookingId/transcript`,
  syncable: false,
  codec: makeRouteCodec({
    lessonBookingId: EntityStringIdRouteCodec,
  }),
});

export const LessonBookingTranscriptPdfRoute = new DynamicRoute<{
  lessonBookingId: EntityStringId,
  position: number,
  timestamp: number,
}>({
  route: `${LESSON_BOOKINGS_ADMIN_PREFIX}/:lessonBookingId/transcript/pdf/:position/:timestamp`,
  syncable: false,
  codec: makeRouteCodec({
    lessonBookingId: EntityStringIdRouteCodec,
    position: PositiveIntegerCodec,
    timestamp: PositiveIntegerCodec,
  }),
});

export const WordAdminRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${ADMIN_PREFIX}/words/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const VocabListsAdminRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: `${ADMIN_PREFIX}/vocab-lists/:languageId`,
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const ViewVocabListRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/vocab-list/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const EditWordRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/word/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const ForbiddenPageRoute = new StaticRoute({
  route: '/http-status/403',
  syncable: true,
});

export const NotFoundPageRoute = new StaticRoute({
  route: '/http-status/404',
  syncable: true,
});

export const MaintenancePageRoute = new StaticRoute({
  route: '/http-status/503',
  syncable: true,
});

export const ColorPaletteRoute = new StaticRoute({
  route: '/color-palette',
  syncable: true,
});

// Teacher sign-up
export const TeacherRecruitmentRoute = new StaticRoute({
  route: '/teach',
  syncable: true,
});

export const TeacherRecruitmentSignUpRoute = new StaticRoute({
  route: '/teach/apply',
  syncable: false,
});

export const TeacherRecruitmentHelpRoute = new StaticRoute({
  route: '/teach/help',
  syncable: false,
});

export const TeacherRecruitmentContactRoute = new StaticRoute({
  route: '/teach/contact',
  syncable: false,
});

// Content directory
export const MaterialsRoute = new StaticRoute({
  route: '/materials',
  syncable: true,
});

/**
 * @deprecated replaced by {@link BrandMaterialsRoute} but kept around so that
 * the old route can redirect to the new one.
 */
export const OrganizationMaterialsRoute = new DynamicRoute<{
  organizationId: EntityStringId,
  languageId: LanguageId,
}>({
  route: '/organizations/:organizationId/materials/:languageId',
  syncable: true,
  codec: makeRouteCodec({
    organizationId: EntityStringIdRouteCodec,
    languageId: LanguageCodec,
  }),
});

export const BrandMaterialsRoute = new DynamicRoute<{
  /**
   * The id of the brand the materials belong to.
   */
  brandId: EntityStringId,
  /**
   * Materials are stored in Curricula that are separated by language Id. The language
   * parameter is used to select from the curricula.
   */
  languageId: LanguageId,
}>({
  route: '/brands/:brandId/materials/:languageId',
  syncable: true,
  codec: makeRouteCodec({
    brandId: EntityStringIdRouteCodec,
    languageId: LanguageCodec,
  }),
});

export const CategoriesRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: '/materials/:languageId',
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const CategoryRoute = new DynamicRoute<{ categoryId: EntityStringId, slug: string }>({
  route: '/materials/:slug/:categoryId',
  syncable: true,
  codec: makeRouteCodec({
    categoryId: EntityStringIdRouteCodec,
    slug: SlugCodec,
  }),
});

export const LevelsRoute = new StaticRoute({
  route: '/materials/levels',
  syncable: true,
});

export const AttachDmmAuthenticationRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/attach-dmm-auth`,
  syncable: false,
});

export const OauthApplicationsRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/oauth-applications`,
  syncable: false,
});

export const OauthApplicationsEditRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/oauth-applications/:id`,
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const ReamazeCustomModuleRoute = new StaticRoute({
  route: `${IFRAME_PREFIX}/reamaze`,
  syncable: false,
});

export const SlackIntegrationSuccess = new StaticRoute({
  route: `${SLACK_INTEGRATION_PREFIX}/success`,
  syncable: false,
});

export const SlackIntegrationCancelled = new StaticRoute({
  route: `${SLACK_INTEGRATION_PREFIX}/cancelled`,
  syncable: false,
});

export const SlackIntegrationFailure = new StaticRoute({
  route: `${SLACK_INTEGRATION_PREFIX}/failure`,
  syncable: false,
});

export const SentenceEditorRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/sentence-editor/:id`,
  syncable: false,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const SentenceListPageRoute = new DynamicRoute<{ languageId: LanguageId }>({
  route: '/admin/sentences/:languageId',
  syncable: true,
  codec: makeRouteCodec({
    languageId: LanguageCodec,
  }),
});

export const BrandEditorRoute = new DynamicRoute<{
  id: EntityStringId;
}>({
  route: `${ADMIN_PREFIX}/brand/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const OrganizationEditorRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/organizations/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const OrganizationListRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/organizations`,
  syncable: true,
});

export const OrganizationCreateRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/organization/create`,
  syncable: true,
});

export const BibliographyListRoute = new StaticRoute({
  route: `${ADMIN_PREFIX}/bibliographies`,
  syncable: true,
});

export const BibliographyEditorRoute = new DynamicRoute<{ id: EntityStringId }>({
  route: `${ADMIN_PREFIX}/bibliographies/:id`,
  syncable: true,
  codec: makeRouteCodec({
    id: EntityStringIdRouteCodec,
  }),
});

export const BrandCreateRoute = new DynamicRoute<{ organizationId: EntityStringId }>({
  route: `${ADMIN_PREFIX}/:organizationId/brand/create`,
  syncable: true,
  codec: makeRouteCodec({
    organizationId: EntityStringIdRouteCodec,
  }),
});
